import { useState, useEffect } from "react";
import { NavLink, withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import PropTypes from "prop-types";
import { injectIntl, defineMessages } from "react-intl";
import ToggleChatButton, { messages as chatMessages } from "./ToggleChatButton";
import { jobGetJob } from "../services/job";
import isEmpty from "lodash.isempty";

// Services
import { userGetToken } from "../services/user";
import FeedbackDialog from "./FeedbackDialog";
import { Button } from "react-bootstrap";
import MenuLocaleSelect from "./MenuLocaleSelect";
import Logo from "./Logo";
import useMessages from "../hooks/useMessages";
import useLocalizedLink from "../hooks/useLocalizedLink";
import useUserAccount from "../hooks/useUserAccount";
import useEnv from "../hooks/useEnv";
import MenuCompanySelect from "./MenuCompanySelect";
import useSessionStorage from "../hooks/useSessionStorage";

export const messages = defineMessages({
    invoices: {
        id: "Laskut",
        defaultMessage: "Laskut"
    },
    messageCenter: {
        id: "Viestikeskus",
        defaultMessage: 'Viestikeskus'
    },
    logout: {
        id: "Kirjaudu ulos",
        defaultMessage: "Kirjaudu ulos"
    },
    returnToFrontPage: {
        id: "Palaa takaisin etusivulle",
        defaultMessage: "Palaa takaisin etusivulle"
    },
    login: {
        id: "Kirjaudu MyRopo-palveluun",
        defaultMessage: "Kirjaudu MyRopo-palveluun"
    },
    dataPrivacy: {
        id: "Tietosuoja",
        defaultMessage: "Tietosuoja"
    },
    dataPrivacyTitle: {
        id: "Ropo - Tietosuojaseloste",
        defaultMessage: "Ropo - Tietosuojaseloste"
    },
    faq: {
        id: "UKK",
        defaultMessage: "UKK"
    },
    unreadMessages: {
        id: "Lukemattomat viestit",
        defaultMessage: "Lukemattomat viestit"
    },
    closeMenu: {
        id: "Sulje valikko",
        defaultMessage: "Sulje valikko"
    },
    mainMenu: {
        id: "Päävalikko",
        defaultMessage: "Päävalikko"
    },
    accountSettings: {
        id: "Tilin asetukset",
        defaultMessage: "Tilin asetukset"
    }
});

const Menu = (props) => {
    // Chat parameters
    const [livechatParameters, SetLivechatParameters] = useState([]);
    const { getUnreadMessageCount } = useMessages();
    const { getLocalizedLink } = useLocalizedLink();
    const { userIsCompany, userIsAuthenticated, userName, userHasAccount } = useUserAccount();
    const lng = props.intl.formatMessage;
    const userAuthorized = userIsAuthenticated();
    const [toggleFeedbackDialog, setToggleFeedbackDialog] = useState(false);
    const privacyLink = getLocalizedLink('LINK_PRIVACY');
    const faqLink = getLocalizedLink('LINK_FAQ');
    const { isEnvTruthy } = useEnv();
    const { setjobTokenUuid } = useSessionStorage();

    const links = [
        {
            label: <span aria-label={lng(messages.invoices)}>{lng(messages.invoices)}</span>,
            link: '/jobsearch',
            notifications: 0,
            onClickFunc: props.menuToggle
        },
        {
            label: <span aria-label={lng(messages.messageCenter)}>{lng(messages.messageCenter)}</span>,
            link: '/messagecenter',
            notifications: getUnreadMessageCount(),
            onClickFunc: props.menuToggle
        }
    ];

    let feedBackToggle = false;

    // Is customer feedback on or off
    if (isEnvTruthy('FEEDBACK_TOGGLE')) {
        feedBackToggle = true;
    }

    let messageCenter = false;
    if (isEnvTruthy('MESSAGECENTER')) {
        messageCenter = true;
    }

    // remove message center from menu if it is disabled
    if (!messageCenter) {
        const index = links.findIndex((menuitem) => menuitem.link === '/messagecenter');
        links.splice(index, 1);
    }

    const menuItems = links.map(function (link) {

        let badge = "";

        if (link.notifications > 0) {
            badge = <span className="notification-badge" aria-label={`${lng(messages.unreadMessages)} ${link.notifications}`}>{link.notifications}</span>;
        }

        return (
            <li key={link.link} className="nav-item mb-2">
                <NavLink className="position-relative"
                         to={link.link} onClick={() => link?.onClickFunc ? link.onClickFunc() : ''}
                >
                    {link.label} {badge}
                </NavLink>
            </li>
        );
    });

    let page = window.location.hash;

    const job = jobGetJob();
    useEffect(() => {
        // Token is mandatory for chat to work
        if (!isEmpty(userGetToken())) {
            if (!isEmpty(job) && window.location.hash === '#/job') {
                SetLivechatParameters([
                    { name: "jobid", value: job.jobid },
                    { name: "token", value: userGetToken() }
                ]);
            } else {
                SetLivechatParameters([
                    { name: "token", value: userGetToken() }
                ]);
            }
        }

    }, [page]);

    /**
     * Log out user and close and hide chat
     */
    function confirmLogout() {
        props.history.push({
            pathname: '/logout',
            state: {
                clearCognito: true
            }
        });
        // Close and hide chat when user logs out
        if (window.LC_API) {
            window.LC_API.close_chat();
            window.LC_API.hide_chat_window();
        }
    }

    /**
     * Open feedback dialog when logging out.
     */
    function handleLogOut() {
        if (feedBackToggle) {
            toggleFeedbackDialog ? setToggleFeedbackDialog(false) : setToggleFeedbackDialog(true);
        } else {
            confirmLogout();
        }
    }

    /**
     * Redirect user to login page via logout.
     */
    function handleLogin() {
        sessionStorage.clear();
        const url = props.location.pathname;
        const jobTokenUuid = url.replace("/job/", "");
        setjobTokenUuid(jobTokenUuid);
        props.history.push({
            pathname: '/',
        });
    }

    return (
        <div id="menu-container">
            <nav id="menu" className={"d-flex flex-column flex-shrink-0 bg-white text-white offcanvas-xl offcanvas-start h-auto " + (props.show ? "show" : "")} aria-label={lng(messages.mainMenu)} role="navigation">
                <div>
                    <NavLink to={userAuthorized ? "/jobsearch": "#"}
                             className="navbar-brand"
                             onClick={props.menuToggle}
                             aria-label={ lng(messages.returnToFrontPage) }
                             tabIndex={0}
                    >
                        <Logo ariaLabel={lng(messages.returnToFrontPage)} height={34} width={128} />
                    </NavLink>
                    {userAuthorized && (
                        <div className="menu-username">
                            {userIsCompany() ? <MenuCompanySelect /> : userName()}
                        </div>
                    )}
                    <ul className="menu nav nav-pills flex-column mr-5">
                        <li className="nav-item-small">
                            {!userAuthorized && (
                                <Button type="button" className={"actionlist-button"} variant='tertiary-menu'
                                        onClick={() => handleLogin()} aria-label={lng(messages.login)} role="menuitem">
                                    <strong>{lng(messages.login)}</strong>
                                </Button>
                            )}
                        </li>
                        {userAuthorized && menuItems}
                    </ul>
                </div>
                <div>
                    <ul className="menu nav nav-pills flex-column" id="menu-bottom">
                        {userAuthorized && !isEmpty(livechatParameters) && (
                            <li className="nav-item mb-2">
                                <ToggleChatButton
                                    visitor={{ name: userName(), email: '' }}
                                    parameters={livechatParameters}
                                    location={'menu'}
                                    aria-label={lng(chatMessages.toggleChat)}
                                    role="menuitem"
                                />
                            </li>
                        )}
                    </ul>
                    <ul className="menu nav nav-pills flex-column" id="menu-footer">
                        {userHasAccount && userAuthorized && (
                            <li className="nav-item-small mb-2">
                                <NavLink
                                    className="position-relative"
                                    to="/user/settings"
                                    onClick={props.menuToggle}
                                >
                                    {lng(messages.accountSettings)}
                                </NavLink>
                            </li>
                        )}
                        {privacyLink && (
                            <li className="nav-item-small mb-2">
                                <a
                                    title={lng(messages.dataPrivacyTitle)} rel="noopener noreferrer"
                                    href={privacyLink}
                                    target="_blank"
                                    className="dataPrivacy-link"
                                    aria-label={lng(messages.dataPrivacy)}
                                    role="menuitem"
                                >
                                    {lng(messages.dataPrivacy)}
                                </a>
                            </li>
                        )}
                        {faqLink && (
                            <li className="nav-item-small mb-2">
                                <a title={lng(messages.faq)}
                                   rel="noopener noreferrer"
                                   href={faqLink}
                                   target="_blank"
                                   className="contactFaq-link"
                                   aria-label={lng(messages.faq)}
                                   role="menuitem"
                                >
                                    {lng(messages.faq)}
                                </a>
                            </li>
                        )}
                        <li className="nav-item mb-2">
                            <MenuLocaleSelect />
                        </li>
                        {userAuthorized && (
                            <li className="nav-item mb-md-4 mb-2">
                                <Button
                                    type="button"
                                    className={"actionlist-button"}
                                    variant='tertiary-menu'
                                    onClick={() => handleLogOut()}
                                    aria-label={lng(messages.logout)}
                                    role="menuitem"
                                >
                                    {lng(messages.logout)}
                                </Button>
                            </li>
                        )}
                    </ul>
                </div>
                <button
                    onClick={props.menuToggle}
                    onKeyDown={props.menuToggle}
                    className="menuclose d-xl-none"
                    aria-label={lng(messages.closeMenu)}
                    aria-controls="menu"
                >
                    <FontAwesomeIcon icon={far.faXmark} />
                </button>
            </nav>
            <div className={"offcanvas-backdrop fade " + (props.show ? "show d-xl-none" : "d-none")}></div>
            {toggleFeedbackDialog && (
                <FeedbackDialog title={lng(messages.logout)} canShow={true} show={toggleFeedbackDialog} handleConfirmDialog={handleLogOut} {...props} />
            )}
        </div>
    );
}

Menu.propTypes = {
    'intl': PropTypes.object.isRequired,
    'show': PropTypes.bool.isRequired,
    'menuToggle': PropTypes.func.isRequired,
    'history': PropTypes.object,
    'location': PropTypes.object
};

export default withRouter(injectIntl(Menu));